import React, { useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";
import { api, errorMessage } from "../lib/api";

export default function WelcomePage() {
  const { token = "" } = useParams();
  const { user, acceptInvite } = useAuth();
  const navigate = useNavigate();
  const [info, setInfo] = useState<{ name: string; email: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/auth/invite/${token}`).then((r) => setInfo(r.data)).catch((e) => setError(errorMessage(e)));
  }, [token]);

  if (user) return <Navigate to="/cellar" replace />;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirm) { toast.error("The two passwords don't match"); return; }
    setBusy(true);
    try {
      await acceptInvite(token, password);
      toast.success("Welcome to your cellar");
      navigate("/cellar", { replace: true });
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };

  return (
    <div className="grain flex min-h-screen items-center px-8 py-16 lg:px-24" data-testid="welcome-page">
      <div className="w-full max-w-md rise">
        <img src="/brand/logo.png" alt="Pierre Hourlier Wines" className="h-24 w-auto" data-testid="auth-logo" />
        {error && (
          <div data-testid="welcome-error">
            <h1 className="mt-3 font-serif text-4xl font-medium tracking-tight sm:text-5xl">This link has expired</h1>
            <p className="mt-4 text-sm text-neutral-600">{error}</p>
            <Button variant="outline" className="mt-8 rounded-none" onClick={() => navigate("/login")} data-testid="welcome-to-login">Go to sign in</Button>
          </div>
        )}
        {!error && !info && <p className="mt-6 font-serif text-2xl text-neutral-500" data-testid="welcome-loading">Opening your invitation…</p>}
        {info && (
          <form onSubmit={submit} data-testid="welcome-form">
            <h1 className="mt-3 font-serif text-4xl font-medium tracking-tight sm:text-5xl">Welcome, {info.name.split(" ")[0]}</h1>
            <p className="mt-3 text-sm text-neutral-600">Your cellar is ready. Choose a password for <strong data-testid="welcome-email">{info.email}</strong> and you're in.</p>
            <div className="mt-10 space-y-6">
              <div className="space-y-2">
                <Label htmlFor="pw" className="eyebrow">Password</Label>
                <Input id="pw" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} className="rounded-none bg-transparent" data-testid="welcome-password-input" />
                <p className="text-xs text-neutral-500">At least 8 characters.</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="pw2" className="eyebrow">Confirm password</Label>
                <Input id="pw2" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required minLength={8} className="rounded-none bg-transparent" data-testid="welcome-confirm-input" />
              </div>
              <Button type="submit" disabled={busy} className="w-full rounded-none py-6 text-base hover-lift" data-testid="welcome-submit-button">{busy ? "One moment…" : "Set password & open my cellar"}</Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
